import { create } from "zustand";
import { createJSONStorage, persist, type PersistStorage } from "zustand/middleware";
import { detectPlaybackMode, type Player, type PlayerError, type PlayerMode, type PlayerSource, type PlayerStatus } from "@hybrid-iptv/player";
import { resolveStreamUrl } from "./streamToken.js";
import { tracePlay } from "./playTrace.js";
import { buildMirrorFallbacks, tokenizeMirrorFallbacks } from "../library/mirrorResolver.js";
import { useSettings } from "../settings/settingsStore.js";
import { activeProfileId, DEFAULT_PROFILE_ID, useProfiles } from "../settings/profileStore.js";
import {
  PlaybackTimeMeter,
  quotaKindFor,
  remainingPlaybackSeconds,
  type PlaybackQuotaKind,
} from "../library/playbackUsageStore.js";
import type { MediaFormat } from "@hybrid-iptv/types";
import { showUpgradePrompt } from "../library/upgradePrompt.js";

/** A channel the user recently watched, kept for the dock's Previous-channel
 * button and the fullscreen recent-strip. Carries everything needed to retune
 * without re-querying the channel catalog. */
export interface RecentChannel {
  id: string;
  name: string;
  number?: number | null;
  logoUrl?: string | null;
  streamUrl: string;
  altStreamUrls?: string[];
}

/** Max recents kept; the dock's channel list shows up to this many. */
export const MAX_RECENT_CHANNELS = 6;

/**
 * Something the viewer actually watched - a channel, a film, an episode, a
 * catch-up replay - with everything needed to start it again. The full-screen
 * strip in the top corner lists these: it used to be live channels only, which
 * was no use to someone who had just been watching films (owner, 2026-09-20).
 */
export interface RecentPlay {
  /** The content reference where there is one, else the stream URL. */
  id: string;
  kind: "live" | "vod" | "episode" | "catchup";
  title: string;
  artworkUrl?: string;
  /** Replayed exactly as it was first played (pre-token, as playSource takes it). */
  source: PlaySourceInput;
  /** Restored before replaying, so watch progress keeps being saved. */
  content: { contentType: "vod" | "episode" | "catchup"; contentRef: string } | null;
  /** Local profile that owned this history row. */
  profileId?: string;
  at: number;
}

/** Max recently-watched items kept across everything. */
export const MAX_RECENT_PLAYS = 12;

/**
 * What {@link PlayerStore.playSource} accepts: the player's `PlayerSource`
 * plus a web-only hint for cross-provider mirror fallbacks (Section 16). When
 * a live channel is mirrored, the call site passes `mirrorKey`
 * (``${activeProviderSourceId()}:${channel.id}``); playSource resolves and
 * tokenizes those fallbacks against their OWN provider and appends them to the
 * alt-url list so a mirrored channel gets a real cross-provider fallback.
 */
export interface PlaySourceInput extends PlayerSource {
  /** Set on live channels that have explicit mirrors (Section 16). When
   *  absent, playSource adds no mirror fallbacks. */
  mirrorKey?: string;
  /**
   * A preview beside what you are browsing, NOT the thing you sat down to
   * watch: the guide's channel preview, and multiview's tiles. Everything else
   * fills the screen the moment it starts (owner, 2026-09-20 — "play should go
   * to full screen from anywhere").
   */
  preview?: boolean;
  /**
   * What the provider measured in the file (episodes only). The website's server
   * then decides the sound without probing the file first (streamToken.ts).
   */
  media?: MediaFormat;
}

/**
 * Reactive player state, fed by the single Player instance's event emitter
 * (technical-spec §6.1). The Player itself is NOT reactive React state — it
 * lives in PlayerProvider and is referenced here so any component can drive
 * it. Components subscribe to the slices they render; nothing re-renders on
 * the player instance identity.
 */
export interface PlayerStore {
  player: Player | null;
  source: PlayerSource | null;
  status: PlayerStatus;
  mode?: PlayerMode;
  currentTime: number;
  duration: number;
  bufferedAhead: number;
  volume: number;
  muted: boolean;
  error: PlayerError | null;
  /** Most-recent-first history of tuned channels (deduped, capped). */
  recentChannels: RecentChannel[];
  /** Most-recent-first history of everything watched (deduped, capped). */
  recentPlays: RecentPlay[];
  /** Start a recently-watched item again (and re-record it as the newest). */
  playRecent: (play: RecentPlay) => Promise<void>;
  /** VOD/episode currently playing — retains the profile that started playback. */
  playingContent: { contentType: "vod" | "episode" | "catchup"; contentRef: string; profileId: string } | null;
  setPlayingContent: (content: { contentType: "vod" | "episode" | "catchup"; contentRef: string } | null) => void;
  /** All profile histories; recentChannels/recentPlays are the active profile's view. */
  recentByProfile: Record<string, RecentLists>;
  playbackKind: PlaybackQuotaKind | null;
  /** Profile that started the current stream, retained across profile switches. */
  playbackProfileId: string | null;
  /** True when a call site assigned content for the next playSource call. */
  playingContentPending: boolean;
  /** Owner of the current progress metadata, retained across profile switches/retries. */
  playbackContentOwnerId: string | null;
  /** Swap visible recent lists when the selected local profile changes. */
  switchProfileView: () => void;
  /** Normalize persisted lists and migrate legacy shared history into "local". */
  hydrateRecentLists: (persisted: PersistedLists) => void;
  /** Called once by PlayerProvider with the constructed Player. */
  attach: (player: Player) => void;
  /** Called by PlayerProvider on unmount. */
  detach: () => void;
  /** Called from the player's statechange event with the full snapshot. */
  sync: (state: {
    status: PlayerStatus;
    mode?: PlayerMode;
    source?: PlayerSource;
    currentTime: number;
    duration: number;
    bufferedAhead: number;
    volume: number;
    muted: boolean;
    error?: PlayerError;
  }) => void;
  /** Resolve a provider URL to a same-origin token URL, load, and play. */
  playSource: (source: PlaySourceInput) => Promise<void>;
  /** Counts requests to fill the screen; PlayerSurface acts on each new one. */
  fullscreenWanted: number;
  /** True while what plays is a preview beside browsing (the guide, multiview). */
  previewing: boolean;
  /**
   * True while a screen is mounted that HAS somewhere for a half-sized picture
   * to live — the guide's preview pane. When it goes, so does the pane, and
   * PlayerSurface moves the video out (picture-in-picture, or nothing).
   */
  previewHost: boolean;
  /** The guide claims the preview pane while it is on screen. */
  setPreviewHost: (active: boolean) => void;
  /** Fill the screen with what is playing (leaving the guide, for instance). */
  requestFullscreen: () => void;
  togglePlayPause: () => Promise<void>;
  seek: (seconds: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  /** Record a tuned channel in the recent history (dedupe, cap). */
  pushRecent: (channel: RecentChannel) => void;
  /** Most recently watched channel different from the one now playing, or null. */
  previousChannel: () => RecentChannel | null;
  /** Retune a channel from the recent list (also re-records it as most recent). */
  tuneRecent: (channel: RecentChannel) => Promise<void>;
  /** Stop playback and clear the dock (playback may continue in background per Media Session). */
  close: () => void;
}

/**
 * Same-origin teardown URL for a transcoded session, or null when not
 * applicable (no source, not transcoded, token missing). The server reaps the
 * ffmpeg child + its segment window immediately on `?stop=1` instead of
 * waiting for the 10-minute idle sweep.
 */
/**
 * Tokenize the cross-provider mirror fallbacks for a channel, if any, against
 * each fallback's OWN provider. Returns the successfully-minted same-origin
 * URLs (or [] when there are no mirrors). Never throws — a mirror failure
 * must not block the primary channel.
 */
async function buildMirrorUrlList(mirrorKey: string | undefined): Promise<string[]> {
  if (!mirrorKey) return [];
  const [sourceId, channelId] = mirrorKey.split(":");
  if (!sourceId || !channelId) return [];
  const providers = useSettings.getState().providers;
  const candidates = buildMirrorFallbacks(sourceId, channelId, providers);
  return tokenizeMirrorFallbacks(candidates);
}

/**
 * Resolve the tokenized stream for a source and return the live stop URL when
 */
export function stopUrlForSource(source: PlayerSource | null): string | null {
  if (!source?.transcoded) return null;
  try {
    const token = new URL(source.url).searchParams.get("token");
    return token ? `${window.location.origin}/api/stream?token=${encodeURIComponent(token)}&stop=1` : null;
  } catch {
    return null;
  }
}

/**
 * True when two sources describe the same stream. The native player hands
 * over a fresh COPY of its source with every state report, twice a second
 * while playing; kept as-is, every `usePlayerStore((s) => s.source)` saw a
 * change and re-rendered — the guide's whole grid included, which kept a
 * 1 GB projector busy a quarter of the time with nothing on screen moving
 * (2026-09-26).
 */
export function sameSource(a: PlayerSource | null | undefined, b: PlayerSource | null | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof PlayerSource>;
  for (const key of keys) {
    const x = a[key];
    const y = b[key];
    if (Array.isArray(x) && Array.isArray(y)) {
      if (x.length !== y.length || x.some((item, i) => item !== y[i])) return false;
    } else if (x !== y) return false;
  }
  return true;
}

export interface RecentLists {
  recentChannels: RecentChannel[];
  recentPlays: RecentPlay[];
}

type PersistedLists = {
  recentByProfile?: Record<string, RecentLists>;
  /** Legacy single-profile lists, migrated to the "local" profile. */
  recentChannels?: RecentChannel[];
  recentPlays?: RecentPlay[];
};

/** localStorage for profile histories, skipping writes when the map is unchanged. */
function unchangedListsSkipped(): PersistStorage<PersistedLists> | undefined {
  const storage = createJSONStorage<PersistedLists>(() => localStorage);
  if (!storage) return undefined;
  let last: Record<string, RecentLists> | null = null;
  return {
    ...storage,
    setItem: (name, value) => {
      if (last && last === value.state.recentByProfile) return;
      last = value.state.recentByProfile ?? null;
      return storage.setItem(name, value);
    },
  };
}

const EMPTY_RECENT_LISTS: RecentLists = { recentChannels: [], recentPlays: [] };

function recentListsFor(
  state: Pick<PlayerStore, "recentChannels" | "recentPlays" | "recentByProfile">,
  profileId: string,
): RecentLists {
  if (profileId === activeProfileId()) {
    return { recentChannels: state.recentChannels, recentPlays: state.recentPlays };
  }
  return state.recentByProfile[profileId] ?? EMPTY_RECENT_LISTS;
}

type RecentListsUpdate = Pick<PlayerStore, "recentByProfile"> & Partial<Pick<PlayerStore, "recentChannels" | "recentPlays">>;

function withRecentLists(
  state: Pick<PlayerStore, "recentChannels" | "recentPlays" | "recentByProfile">,
  recentChannels: RecentChannel[],
  recentPlays: RecentPlay[],
  profileId = activeProfileId(),
): RecentListsUpdate {
  const lists = { recentChannels, recentPlays };
  return {
    ...(profileId === activeProfileId() ? lists : {}),
    recentByProfile: { ...state.recentByProfile, [profileId]: lists },
  };
}

let playbackMeter: PlaybackTimeMeter | null = null;
let meterKind: PlaybackQuotaKind | null = null;

function resetPlaybackMeter(): void {
  playbackMeter?.reset();
  playbackMeter = null;
  meterKind = null;
}

/**
 * The recent list with this play at its head, deduped and capped.
 *
 * Live channels carry no artwork through PlayerSource, so their logo is taken
 * from the channel history, which every live call site fills in just before
 * playing (pushRecent).
 */
function rememberPlay(state: PlayerStore, source: PlaySourceInput): RecentPlay[] {
  // Never remembered as a preview: replaying from the strip is a deliberate
  // "watch this", which fills the screen like every other play.
  const { preview: _preview, ...replayable } = source;
  const content = source.type === "vod" && state.playingContent?.profileId === state.playbackProfileId
    ? state.playingContent
    : null;
  const id = content ? `${content.contentType}:${content.contentRef}` : source.url;
  const artwork =
    source.artworkUrl ??
    (source.type === "live" ? state.recentChannels.find((c) => c.name === source.title)?.logoUrl : null);
  const contentOwnerId = content?.profileId ?? state.playbackProfileId ?? activeProfileId();
  const play: RecentPlay = {
    id,
    kind: source.type === "live" ? "live" : (content?.contentType ?? "vod"),
    title: source.title ?? "Now playing",
    ...(artwork ? { artworkUrl: artwork } : {}),
    source: replayable,
    content,
    profileId: contentOwnerId,
    at: Date.now(),
  };
  return [play, ...state.recentPlays.filter((p) => p.id !== id)].slice(0, MAX_RECENT_PLAYS);
}

export const usePlayerStore = create<PlayerStore>()(
  persist(
    (set, get) => ({
      player: null,
      source: null,
      status: "idle",
      currentTime: 0,
      duration: 0,
      bufferedAhead: 0,
      volume: 1,
      muted: false,
      error: null,
      recentChannels: [],
      recentPlays: [],
      recentByProfile: { [activeProfileId()]: EMPTY_RECENT_LISTS },
      playingContent: null,
      playbackProfileId: null,
      playingContentPending: false,
      playbackContentOwnerId: null,
      playbackKind: null,

      switchProfileView: () => {
        const profileId = activeProfileId();
        const current = get();
        const lists = current.recentByProfile[profileId] ?? EMPTY_RECENT_LISTS;
        set({
          ...lists,
          recentByProfile: { ...current.recentByProfile, [profileId]: lists },
        });
      },
      hydrateRecentLists: (persisted) => {
        const legacyLists = {
          recentChannels: persisted.recentChannels ?? [],
          recentPlays: persisted.recentPlays ?? [],
        };
        const recentByProfile = { ...persisted.recentByProfile };
        if (!recentByProfile[DEFAULT_PROFILE_ID]) recentByProfile[DEFAULT_PROFILE_ID] = legacyLists;
        for (const lists of Object.values(recentByProfile)) {
          lists.recentPlays = lists.recentPlays.map((play) => ({
            ...play,
            profileId: play.profileId ?? DEFAULT_PROFILE_ID,
          }));
        }
        const currentProfileId = activeProfileId();
        const activeLists = recentByProfile[currentProfileId] ?? EMPTY_RECENT_LISTS;
        set({
          ...activeLists,
          recentByProfile,
        });
      },

      setPlayingContent: (playingContent) => {
        const profileId = activeProfileId();
        set({
          playingContent: playingContent ? { ...playingContent, profileId } : null,
          playbackContentOwnerId: playingContent ? profileId : null,
          playingContentPending: true,
        });
      },

      attach: (player) => set({ player }),
      detach: () => {
        resetPlaybackMeter();
        set({
          player: null,
          source: null,
          status: "idle",
          error: null,
          playingContent: null,
          playbackProfileId: null,
          playingContentPending: false,
          playbackContentOwnerId: null,
          playbackKind: null,
        });
      },

      sync: (state) => {
        const current = get();
        const source = sameSource(state.source, current.source) ? current.source : state.source ?? current.source;
        const playingContent = current.playingContent?.profileId === current.playbackProfileId
          ? current.playingContent
          : null;
        if (current.playbackKind !== meterKind) {
          meterKind = current.playbackKind;
          playbackMeter = meterKind ? new PlaybackTimeMeter(meterKind) : null;
        }
        const accounting = current.playbackKind
          ? { kind: current.playbackKind, exhausted: playbackMeter ? playbackMeter.sample(state.status) : false }
          : { kind: source ? quotaKindFor(source.type, playingContent?.contentType) : null, exhausted: false };
        const exhausted = accounting.exhausted && state.status === "playing";
        set({
          status: exhausted ? "error" : state.status,
          mode: state.mode,
          source,
          playingContent,
          ...(source === null ? { playbackKind: null } : {}),
          currentTime: state.currentTime,
          duration: state.duration,
          bufferedAhead: state.bufferedAhead,
          volume: state.volume,
          muted: state.muted,
          error: exhausted
            ? { code: "exhausted", message: `Free ${accounting.kind} playback limit reached. Unlock Paid mode to continue.`, recoverable: false }
            : state.error ?? null,
        });
        if (exhausted) {
          current.player?.stop();
          showUpgradePrompt(accounting.kind!);
        }
      },

      fullscreenWanted: 0,
      previewing: false,
      previewHost: false,
      setPreviewHost: (active) => set({ previewHost: active }),
      requestFullscreen: () =>
        set((state) => {
          // Pressing Watch is the active profile's play, like playSource.
          const profileId = activeProfileId();
          const lists = recentListsFor(state, profileId);
          const commitPreview = state.previewing && state.source !== null;
          const recentPlays = commitPreview
            ? rememberPlay({ ...state, ...lists, playingContent: state.playingContent?.profileId === profileId ? state.playingContent : null, playbackProfileId: profileId }, state.source!)
            : lists.recentPlays;
          return {
            fullscreenWanted: state.fullscreenWanted + 1,
            // Filling the screen is the viewer COMMITTING to a preview: the guide's
            // channel preview is not history while they are still browsing past it,
            // but the moment they press Watch it is the thing they are watching.
            previewing: false,
            ...(commitPreview ? withRecentLists(state, lists.recentChannels, recentPlays, profileId) : {}),
          };
        }),
      playSource: async (source) => {
        const player = get().player;
        tracePlay("playSource", `${source.type} ${source.title ?? ""}`);
        if (!player) {
          tracePlay("no player attached");
          throw new Error("Player is not attached.");
        }
        const current = get();
        // Every new play, retries included, belongs to the profile active NOW.
        // A stream another profile started may still be playing after a
        // switch; charging this play to it leaked history between profiles.
        const activeId = activeProfileId();
        const currentContent = current.playingContent;
        const currentContentOwnerId = currentContent?.profileId ?? null;
        const pendingContent = current.playingContentPending && currentContentOwnerId === activeId;
        const retryingSameVod =
          source.type === "vod" &&
          current.source?.url === source.url &&
          current.source?.type === "vod" &&
          currentContent !== null &&
          currentContentOwnerId === activeId &&
          current.playbackContentOwnerId === currentContentOwnerId;
        const playingContent = source.type === "vod" && (pendingContent || retryingSameVod)
          ? currentContent
          : null;
        const kind = quotaKindFor(source.type, playingContent?.contentType);
        if (remainingPlaybackSeconds(kind) <= 0) {
          // Every caller swallows this rejection, so the press looked dead:
          // the prompt is what the viewer sees (owner, 2026-09-29).
          showUpgradePrompt(kind);
          throw new Error(`Free ${kind} playback limit reached. Unlock Paid mode to continue.`);
        }
        resetPlaybackMeter();
        meterKind = kind;
        playbackMeter = new PlaybackTimeMeter(kind);
        const contentOwnerId = playingContent?.profileId ?? null;
        const profileId = activeId;
        const lists = recentListsFor(current, profileId);
        const recentState = { ...current, ...lists };
        const recentPlays = source.preview
          ? lists.recentPlays
          : rememberPlay({ ...recentState, playingContent, playbackProfileId: profileId }, source);
        set({
          source,
          playbackProfileId: profileId,
          playingContent,
          playingContentPending: false,
          playbackContentOwnerId: contentOwnerId,
          playbackKind: kind,
          status: "loading",
          currentTime: 0,
          duration: 0,
          error: null,
          previewing: Boolean(source.preview),
          // Anything but a preview fills the screen (see PlaySourceInput.preview).
          fullscreenWanted: source.preview ? current.fullscreenWanted : current.fullscreenWanted + 1,
          // ... and is remembered. Recorded HERE rather than at each call site so
          // every route lands in the history of the profile that started playback.
          ...(!source.preview
            ? withRecentLists(current, lists.recentChannels, recentPlays, profileId)
            : {}),
        });
        // EVERY url the player can load must be a same-origin tokenized proxy URL.
        // The fallback list is no exception: a raw provider URL in altUrls makes
        // the browser connect DIRECTLY to the provider on fallback — no token, no
        // connection gate, invisible in server logs. Measured 2026-08-26: that is
        // the connection surge — five concurrent provider sessions from one
        // browser, saturating the account's connection cap, starving every stream
        // (the provider panel showed them from the viewer's IP, not the VPS).
        // Resolve primary and alts in parallel; a failing alt is dropped, not
        // fatal — it is a fallback, not the request.
        tracePlay("resolving");
        const [resolved, alts, mirrorAlts] = await Promise.all([
          resolveStreamUrl(source.url, source.media),
          Promise.all((source.altUrls ?? []).map((alt) => resolveStreamUrl(alt).catch(() => null))),
          // Cross-provider mirrors (Section 16): tokenized against the fallback's
          // OWN provider so a mirrored channel carries a real fallback on another
          // account. Same discipline as alts — a failing mirror is dropped.
          buildMirrorUrlList(source.mirrorKey),
        ]);
        const resolvedAlts = alts.filter((alt): alt is NonNullable<typeof alt> => alt !== null);
        // The tokenized proxy URL (/api/stream?token=…) has no file extension, so
        // sniff the engine from the ORIGINAL provider URL — .m3u8 → hls, .ts →
        // mpegts, .mp4/.webm → native — otherwise every live channel would be
        // misdetected as raw MPEG-TS.
        const format = source.format ?? detectPlaybackMode(source.url);
        tracePlay("resolved", get().player === player ? "same player" : "the player was replaced meanwhile");
        await player.load({
          ...source,
          url: resolved.url,
          altUrls: [...resolvedAlts.map((alt) => alt.url), ...mirrorAlts],
          format,
          // Transcodes are live-shaped pipes; the player must treat them as live.
          transcoded: resolved.transcoded || source.transcoded,
        });
        // Full level, always: the device's own volume control is the only one
        // (owner, 2026-09-20 — the slider and mute button took space on screen and
        // every remote and phone has volume keys of its own).
        tracePlay("loaded", `destroyed=${(player as unknown as { destroyed?: boolean }).destroyed === true} status=${player.getState().status}`);
        player.setVolume(1);
        player.setMuted(false);
        await player.play();
        tracePlay("play asked");
      },

      togglePlayPause: async () => {
        const { player, status } = get();
        if (!player) return;
        if (status === "playing") player.pause();
        else await player.play();
      },

      seek: (seconds) => get().player?.seek(seconds),

      pushRecent: (channel) =>
        set((state) => {
          // Callers record the channel BEFORE they start it, so it goes to
          // the profile watching now, never the one whose stream is ending.
          const recentChannels = [channel, ...state.recentChannels.filter((item) => item.id !== channel.id)]
            .slice(0, MAX_RECENT_CHANNELS);
          return withRecentLists(state, recentChannels, state.recentPlays);
        }),

      previousChannel: () => {
        const state = get();
        const lists = recentListsFor(state, state.playbackProfileId ?? activeProfileId());
        return lists.recentChannels.find((channel) => channel.name !== state.source?.title) ?? null;
      },

      playRecent: async (play) => {
        const profileId = play.profileId ?? activeProfileId();
        const content = play.content ? { ...play.content, profileId } : null;
        set({
          playingContent: content,
          playbackProfileId: profileId,
          playbackContentOwnerId: content ? profileId : null,
          playingContentPending: true,
        });
        await get().playSource(play.source);
      },

      tuneRecent: async (channel) => {
        get().pushRecent(channel);
        await get().playSource({
          url: channel.streamUrl,
          type: "live",
          title: channel.name,
          altUrls: channel.altStreamUrls,
        });
      },

      setVolume: (volume) => {
        const { player, muted } = get();
        if (!player) return;
        const next = Math.max(0, Math.min(1, volume));
        player.setVolume(next);
        if (next > 0 && muted) {
          player.setMuted(false);
          set({ muted: false });
        }
      },

      toggleMute: () => {
        const { player, muted } = get();
        if (!player) return;
        player.setMuted(!muted);
        set({ muted: !muted });
      },

      close: () => {
        const { player, source } = get();
        resetPlaybackMeter();
        const stopUrl = stopUrlForSource(source);
        if (stopUrl) void fetch(stopUrl, { keepalive: true }).catch(() => undefined);
        player?.stop();
        set({
          source: null,
          status: "idle",
          currentTime: 0,
          duration: 0,
          error: null,
          playingContent: null,
          playbackProfileId: null,
          playingContentPending: false,
          playbackContentOwnerId: null,
          playbackKind: null,
        });
      },
    }),
    {
      name: "slique-player-store",
      partialize: (state) => ({ recentByProfile: state.recentByProfile }),
      storage: unchangedListsSkipped(),
      merge: (persisted, current) => ({ ...current, ...(persisted as Partial<PlayerStore>) }),
      version: 2,
      migrate: (persisted) => {
        const state = (persisted ?? {}) as PersistedLists;
        const recentByProfile = { ...(state.recentByProfile ?? {}) };
        if (!recentByProfile[DEFAULT_PROFILE_ID]) {
          recentByProfile[DEFAULT_PROFILE_ID] = {
            recentChannels: state.recentChannels ?? [],
            recentPlays: state.recentPlays ?? [],
          };
        }
        return { recentByProfile };
      },
      onRehydrateStorage: () => (state, error) => {
        if (!state || error) return;
        state.hydrateRecentLists({
          recentByProfile: state.recentByProfile,
          recentChannels: state.recentChannels,
          recentPlays: state.recentPlays,
        });
      },
    },
  ),
);

useProfiles.subscribe((state, previous) => {
  if (state.activeProfileId !== previous.activeProfileId) {
    usePlayerStore.getState().switchProfileView();
  }
});

